import clsx from 'clsx';
import {
  type ChangeEvent,
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

import { RadioGroupContext } from './RadioGroupContext.js';
import * as styles from './styles.sty.js';
import { VisuallyHidden } from '../../core/components/visually-hidden/VisuallyHidden.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useId } from '../../core/hooks/useId.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';
import { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { DOMProps } from '../../core/types/dom.js';
import type { FocusEvents } from '../../core/types/events.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { isDev } from '../../core/utils/is-node-environment.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';
import { fieldTokens as fieldColorsCSS } from '../../styles/field.sty.js';
import { Text } from '../../typography/text/Text.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { useFormFieldValidation } from '../forms-core/hooks/useFormFieldValidation.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import type { FormControlRef } from '../shared-types.js';

const COMPONENT_NAME = 'Radio';

// #region Typings
/**
 * Properties for the Radio component
 * @public
 */
export type RadioProps = Pick<
  FormControlProps<string, never>,
  'disabled' | 'value'
> &
  DOMProps &
  StylingProps &
  WithChildren &
  DataTestId &
  FocusEvents &
  AriaLabelingProps &
  BehaviorTrackingProps;
//#endregion

/**
 * Radio buttons allow users to select one option from
 * a group of related options. To group options, wrap the
 * `Radio` components inside a `RadioGroup` component.
 * If you want to give the user more than 4 options,
 * consider using a `Select` component.
 * @public
 */
export const Radio = /* @__PURE__ */ forwardRef<FormControlRef, RadioProps>(
  (props, forwardedRef) => {
    const fallbackId = useId();
    const {
      disabled: propDisabled,
      id = fallbackId,
      children,
      value,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'aria-label': ariaLabelProp,
      'aria-labelledby': ariaLabelledByProp,
      'aria-describedby': ariaDescribedByProp,
      'aria-details': ariaDetailsProp,
      ...remainingProps
    }: RadioProps = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);

    const { dispatch: messagesContextDispatch } = useFormFieldMessagesContext();

    const radioGroupContext = useContext(RadioGroupContext);

    if (isDev() && !radioGroupContext) {
      // eslint-disable-next-line no-console
      console.warn(
        '<Radio /> can only be used as a child element of <RadioGroup />',
      );
    }

    const {
      isDisabled,
      ariaDisabled,
      controlState,
      hasError,
      readOnly,
      name: groupName,
      required: groupRequired,
      value: groupValue,
      onChange,
    } = radioGroupContext || {};

    // Imperative Handle Construction
    const radioLabelRef = useRef<HTMLLabelElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(forwardedRef, (): FormControlRef<HTMLLabelElement> => {
      return {
        element: radioLabelRef.current,
        inputRef: inputRef.current,
        focus() {
          inputRef.current?.focus();
        },
        validate() {
          if (controlState !== undefined) {
            return;
          }

          inputRef.current?.checkValidity();
          messagesContextDispatch?.({
            type: FormFieldMessagesAction.SETISTOUCHED,
            payload: { isTouched: true },
          });
        },
      };
    });

    const {
      onChange: validationOnChange,
      onBlur: validationOnBlur,
      onInvalid: validationOnInvalid,
      ariaProps,
    } = useFormFieldValidation(
      inputRef,
      { groupRequired },
      {
        'aria-label': ariaLabelProp,
        'aria-labelledby': ariaLabelledByProp,
        'aria-describedby': ariaDescribedByProp,
        'aria-details': ariaDetailsProp,
      },
    );

    /** Register every single input to the form error context to ensure proper validation. */
    useEffect(() => {
      messagesContextDispatch?.({
        type: FormFieldMessagesAction.ADDFORMCONTROLID,
        payload: { controlId: id },
      });

      return () =>
        messagesContextDispatch?.({
          type: FormFieldMessagesAction.REMOVEFORMCONTROLID,
          payload: { controlId: id },
        });
    }, [id]);

    const disabled = propDisabled ?? isDisabled;
    const disabledOrAriaDisabled = disabled || ariaDisabled || false;

    const checked = value === groupValue;

    function handleChange(e: ChangeEvent<HTMLInputElement>) {
      if (readOnly) {
        return;
      }

      onChange?.(e.target.value);
      !controlState && validationOnChange?.();
    }

    let variant: 'accent' | 'emphasized' | 'default' = 'default';
    let color: 'critical' | 'primary' | 'neutral' = 'neutral';

    if (checked) {
      variant = 'accent';
    } else if (hasError) {
      variant = 'emphasized';
    }

    if (hasError) {
      color = 'critical';
    } else if (!readOnly && checked) {
      color = 'primary';
    }

    const { focusProps, focusClassName, isFocusVisible } = useFocusRing({
      color,
      disabled,
    });

    return (
      <label
        data-testid={dataTestId}
        htmlFor={id}
        ref={radioLabelRef}
        className={clsx(
          styles.label,
          {
            [styles.disabled]: disabledOrAriaDisabled,
            [styles.checked]: checked,
          },
          consumerClassName,
        )}
        style={consumerStyle}
        {...behavioralTrackingProps}
        data-dt-component={COMPONENT_NAME}
      >
        <VisuallyHidden>
          <input
            ref={inputRef}
            checked={checked}
            name={groupName}
            value={value}
            type="radio"
            disabled={disabled}
            aria-disabled={ariaDisabled}
            required={groupRequired}
            id={id}
            onChange={handleChange}
            onInvalid={(e) => {
              if (controlState) {
                e.preventDefault(); // Prevents the browser validation pop-up
                return;
              }

              validationOnInvalid(e);
            }}
            readOnly={readOnly}
            {..._mergeProps(
              restProps,
              focusProps,
              controlState
                ? {}
                : {
                    ...ariaProps,
                    onBlur: validationOnBlur,
                  },
            )}
          />
        </VisuallyHidden>
        <div
          className={clsx(
            styles.knob({
              disabled: disabledOrAriaDisabled,
              checked,
              isFocusVisible,
              hasError,
              readOnly,
            }),
            fieldColorsCSS({
              disabled: disabledOrAriaDisabled,
              variant,
              color,
            }),
            focusClassName,
          )}
        >
          <svg
            className={clsx(
              styles.innerKnob,
              fieldColorsCSS({
                color: hasError ? 'critical' : 'primary',
                disabled: disabledOrAriaDisabled,
                variant: 'accent',
              }),
            )}
            viewBox="0 0 100 100"
          >
            <circle r={isFocusVisible ? '20' : '25'} cx="50" cy="50" />
          </svg>
        </div>
        <Text as="span" textStyle="base">
          {children}
        </Text>
      </label>
    );
  },
);

(Radio as typeof Radio & { displayName: string }).displayName = COMPONENT_NAME;
